import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useCatalog } from "@/hooks/useCatalog";
import ProductCard from "@/components/ProductCard";
import { ProductCardSkeleton } from "@/components/Skeleton";
import { GramCard, GramModal } from "@/components/GramShop";
import { fetchGramTeas } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { CATEGORIES, GIFT_SETS, RITUALS, LIFESTYLE } from "@/data/content";

const HEADERS = {
  honey: { title: "Honey", sub: "Raw, infused, whipped, creamed & documented Manuka honey MGO 234+.", img: LIFESTYLE.honey },
  tea: { title: "Loose-Leaf Tea", sub: "Four small-batch blends — bright hot, refreshing over ice.", img: LIFESTYLE.teaRitual },
  "body-care": { title: "Body Care", sub: "JASAY Botanica botanical body care for restorative rituals.", img: LIFESTYLE.selfCare },
  "oils-personal-care": { title: "Oils & Personal Care", sub: "Small-batch golden jojoba oils, pure and infused.", img: LIFESTYLE.bodyOil },
  "gift-sets": { title: "Gift Sets", sub: "Curated pairings of our real products, ready to give.", img: LIFESTYLE.cozy },
  "wellness-rituals": { title: "Wellness Rituals", sub: "Simple daily moments, made a little more beautiful.", img: LIFESTYLE.candle },
};

export default function Shop() {
  const { category = "honey" } = useParams();
  const { byCategory, bySlug, products, isLoading } = useCatalog();
  const meta = HEADERS[category] || HEADERS.honey;
  const cat = CATEGORIES.find((c) => c.slug === category);

  useEffect(() => {
    document.title = `${meta.title} · Washington Family Provisions`;
  }, [meta.title]);

  const isGift = category === "gift-sets";
  const isRitual = category === "wellness-rituals";
  const list = !isGift && !isRitual ? byCategory(category) : [];

  // Add gram-variant teas (e.g. Moroccan Mint Green Tea) to the existing Loose Teas page only.
  const [gramModal, setGramModal] = useState(null);
  const { data: gtData } = useQuery({ queryKey: ["gram-teas"], queryFn: fetchGramTeas, enabled: category === "tea" });
  const gramTeas = category === "tea" ? (gtData?.gram_teas || []) : [];

  return (
    <div>
      <section className="relative grain overflow-hidden bg-sand">
        <img src={meta.img} alt={meta.title} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal>
            <nav className="mb-4 text-xs uppercase tracking-[0.2em] text-wood-500">
              <Link to="/" className="hover:text-honey-dark">Home</Link> / <span className="text-wood-900">{meta.title}</span>
            </nav>
            <h1 className="font-serif text-5xl text-wood-900 md:text-7xl">{meta.title}</h1>
            <p className="mt-4 max-w-xl text-wood-700">{cat?.tagline ? meta.sub : meta.sub}</p>
          </Reveal>
        </div>
      </section>

      {isLoading ? (
        <section className="mx-auto max-w-7xl px-5 py-16 md:px-8">
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4 md:gap-6">
            {[...Array(8)].map((_, i) => <ProductCardSkeleton key={i} />)}
          </div>
        </section>
      ) : isGift ? (
        <section className="mx-auto max-w-7xl px-5 py-16 md:px-8">
          <div className="grid gap-8 md:grid-cols-2">
            {GIFT_SETS.map((set) => {
              const items = set.slugs.map(bySlug).filter(Boolean);
              const total = items.reduce((a, p) => a + (p.price_min || 0), 0);
              return (
                <Reveal key={set.id}>
                  <div className="rounded-3xl bg-sand p-7" data-testid={`giftset-${set.id}`}>
                    <h2 className="font-serif text-3xl text-wood-900">{set.title}</h2>
                    <p className="mt-2 text-wood-700">{set.blurb}</p>
                    <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 md:grid-cols-2 lg:grid-cols-4">
                      {items.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                    <p className="mt-6 text-sm text-wood-500">Build this set — add each item to your cart above. From <span className="font-semibold text-wood-900">${total.toFixed(2)}</span>.</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      ) : isRitual ? (
        <section className="mx-auto max-w-7xl px-5 py-16 md:px-8">
          <div className="flex flex-col gap-12">
            {RITUALS.map((r) => (
              <Reveal key={r.id}>
                <div className="grid items-center gap-8 md:grid-cols-2" data-testid={`ritual-${r.id}`}>
                  <div className="overflow-hidden rounded-3xl">
                    <img src={LIFESTYLE[r.image]} alt={r.title} className="aspect-[4/3] w-full object-cover" />
                  </div>
                  <div>
                    <span className="font-serif text-5xl text-honey">{r.number}</span>
                    <h2 className="mt-2 font-serif text-3xl text-wood-900">{r.title}</h2>
                    <p className="mt-3 text-wood-700">{r.body}</p>
                    <Link to={`/shop/${r.slug}`} className="mt-5 inline-flex rounded-full bg-wood-900 px-7 py-3.5 text-sm font-medium text-cream hover:bg-honey-dark">Shop this ritual</Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      ) : list.length === 0 ? (
        <div className="mx-auto max-w-3xl px-5 py-24 text-center">
          <h2 className="font-serif text-3xl text-wood-900">New arrivals coming soon</h2>
          <p className="mt-3 text-wood-700">We're preparing this collection. Explore our honey and teas in the meantime.</p>
          <Link to="/shop/honey" className="mt-6 inline-flex rounded-full bg-wood-900 px-7 py-3.5 text-sm text-cream hover:bg-honey-dark">Shop Honey</Link>
        </div>
      ) : (
        <section className="mx-auto max-w-7xl px-5 py-16 md:px-8">
          <p className="mb-8 text-sm text-wood-500">{list.length + gramTeas.length} product{list.length + gramTeas.length > 1 ? "s" : ""}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4 md:gap-6">
            {list.map((p, i) => <Reveal key={p.id} delay={(i % 4) * 0.05}><ProductCard product={p} /></Reveal>)}
            {gramTeas.map((t, i) => (
              <Reveal key={t.id} delay={((list.length + i) % 4) * 0.05}>
                <GramCard product={t} onChoose={setGramModal} testid={`gram-tea-card-${t.id}`} />
              </Reveal>
            ))}
          </div>
        </section>
      )}
      {gramModal && <GramModal product={gramModal} onClose={() => setGramModal(null)} />}
    </div>
  );
}
